import React, { useMemo } from 'react'
import {
  pocketFloorFromCej,
  PerioSurfaceSites,
  toFivePointSeries,
} from '@utils/perio-line.utils'
import {
  PERIO_REF_STROKE,
  PERIO_SERIES_A_STROKE,
  PERIO_SERIES_B_STROKE,
  PERIO_VIEW_H,
  PERIO_VIEW_W,
  perioFivePointsToPath,
} from './tooth-perio-line.geometry'
import './ToothPerioLine.css'

interface ToothPerioLineProps {
  toothId: number
  sitesA: PerioSurfaceSites
  sitesB?: PerioSurfaceSites
  /** Upper (1–16): line near bottom. Lower (17–32): line near top. */
  isUpper: boolean
}

const ToothPerioLine = ({
  toothId,
  sitesA,
  sitesB,
  isUpper,
}: ToothPerioLineProps): React.ReactElement => {
  const refPath = useMemo(
    (): string =>
      perioFivePointsToPath(toFivePointSeries(sitesA.gm), isUpper, toothId),
    [sitesA.gm, isUpper, toothId],
  )
  const pathA = useMemo(
    (): string =>
      perioFivePointsToPath(
        toFivePointSeries(pocketFloorFromCej(sitesA.gm, sitesA.pd)),
        isUpper,
        toothId,
      ),
    [sitesA.gm, sitesA.pd, isUpper, toothId],
  )
  const pathB = useMemo((): string | null => {
    if (!sitesB) {
      return null
    }
    return perioFivePointsToPath(
      toFivePointSeries(pocketFloorFromCej(sitesB.gm, sitesB.pd)),
      isUpper,
      toothId,
    )
  }, [sitesB, isUpper, toothId])

  return (
    <svg
      className="tooth-perio-line"
      viewBox={`0 0 ${PERIO_VIEW_W} ${PERIO_VIEW_H}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <path
        d={refPath}
        fill="none"
        stroke={PERIO_REF_STROKE}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={pathA}
        fill="none"
        stroke={PERIO_SERIES_A_STROKE}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
      {pathB && (
        <path
          d={pathB}
          fill="none"
          stroke={PERIO_SERIES_B_STROKE}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  )
}

export default ToothPerioLine
